import React, { useState } from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { SkeletonText } from '../../shared/Skeleton';
import { EmptyState, Pager, usePaged, tintClasses, fmtNumber, fmtDate } from '../kit';
import { SURFACE } from '../layout';
import CardHeader from './CardHeader';
import { BlockError } from './states';
import DrillNumber from './DrillNumber';
import RailRow from './RailRow';
import { CARD_PAGE_SIZE } from './cardPageSize';
import { dueTone, isDoneStatus, statusTone } from '../metaTone';

// Work we owe them (spec §3). Data: fn_client_context.work — { open, overdue,
// stalled, blocked, needs_assistance, waiting_on_client, by_service:{}, items:[…] }.
// The COUNTS are the truth; `items` is a SAMPLE, so a count is never derived from
// items.length. Reads its slice from the context call the page already made.

// The count buckets, in consequence order, each with the label shown on its chip and the
// meaning tint. Labels are sentence case as displayed ("Waiting on client"). Zero
// buckets are omitted so the row says only what is true right now.
const BUCKETS = [
  ['overdue', 'Overdue', 'warm'],
  ['blocked', 'Blocked', 'warm'],
  ['stalled', 'Stalled', 'warn'],
  ['needs_assistance', 'Need assistance', 'warn'],
  ['waiting_on_client', 'Waiting on client', 'waiting'],
  ['open', 'Open', 'neutral'],
];

/**
 * The count chips, from the count fields (never from items.length); zero, missing
 * and non-numeric buckets are dropped.
 * @param {object|null} [work]  fn_client_context.work
 * @returns {Array<{key:string,count:number,label:string,tint:string}>}
 */
export const countChips = (work) =>
  BUCKETS.filter(([k]) => Number(work?.[k]) > 0).map(([key, label, tint]) => ({
    key, count: Number(work[key]), label, tint,
  }));

/**
 * Whether a sampled work item belongs to one of the count chips, read from the item's own fields.
 * (The chip COUNTS come from the backend; the items are only a sample, so this filters the sample.)
 *
 * @param {object} it    A work item.
 * @param {string} key   overdue · blocked · stalled · needs_assistance · waiting_on_client · open
 * @param {Date} [now]
 * @returns {boolean}
 */
export const itemInBucket = (it, key, now = new Date()) => {
  const status = String(it?.status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const done = isDoneStatus(it?.status);
  switch (key) {
    case 'open': return !done;
    case 'overdue': return !done && dueTone(it?.due, now) === 'red';
    case 'blocked': return status === 'blocked';
    case 'stalled': return it?.stalled === true || status === 'stalled';
    case 'needs_assistance': return it?.needs_assistance === true || status === 'needs_assistance';
    case 'waiting_on_client': return status === 'waiting_on_client';
    default: return true;
  }
};

// A selected chip turns SOLID in its own meaning colour (the idle chip is a tinted outline).
const SOLID = {
  warm: 'border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-500 dark:text-gray-950',
  warn: 'border-amber-500 bg-amber-500 text-gray-950 dark:border-amber-400 dark:bg-amber-400',
  waiting: 'border-cyan-600 bg-cyan-600 text-white dark:border-cyan-400 dark:bg-cyan-400 dark:text-gray-950',
  positive: 'border-emerald-600 bg-emerald-600 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-gray-950',
  cool: 'border-gray-600 bg-gray-600 text-white dark:border-gray-300 dark:bg-gray-300 dark:text-gray-950',
  neutral: 'border-gray-700 bg-gray-700 text-white dark:border-gray-300 dark:bg-gray-300 dark:text-gray-950',
};

// Service keys are data (the set grows), so they are title-cased, not looked up — only
// well-known acronyms are upper-cased so "seo" reads "SEO", not "Seo".
const ACRONYMS = new Set(['seo', 'ppc', 'sem', 'sms', 'crm', 'cdp', 'ai', 'api', 'ga4', 'ux', 'ui']);

// The backend's catch-all bucket for work with no service. Read as a label it looks like
// the owner label "Unassigned" shown beside it, so only this one known key is renamed.
const NO_SERVICE_KEY = 'unassigned';

/**
 * A service key as a label: underscores to spaces, words capitalised, acronyms upper-cased.
 * The "unassigned" bucket reads "No service" so it is not mistaken for the owner label.
 * @param {string} key  e.g. "social_media", "seo", "unassigned"
 * @returns {string}    e.g. "Social Media", "SEO", "No service"
 */
export const serviceLabel = (key) => {
  if (String(key).trim().toLowerCase() === NO_SERVICE_KEY) return 'No service';
  return String(key)
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

/** "SEO 1 · Social Media 5" from by_service; '' when absent. */
export const serviceLine = (work) => {
  const by = work?.by_service;
  if (!by || typeof by !== 'object') return '';
  return Object.entries(by)
    .map(([svc, n]) => `${serviceLabel(svc)} ${fmtNumber(n)}`)
    .join(' · ');
};

/**
 * The meaning tint of one work item's left rail. Blocked outranks stalled, stalled
 * outranks waiting; anything else is calm.
 * @param {{status?:string, stalled?:boolean}} item
 * @returns {"warm"|"warn"|"waiting"|"neutral"}
 */
export const workItemTint = (item) => {
  if (item?.status === 'blocked') return 'warm';
  if (item?.stalled) return 'warn';
  if (item?.status === 'waiting_on_client') return 'waiting';
  return 'neutral';
};

// Status arrives as a machine token ("in_progress") — spaced words, never the raw token.
const humanizeStatus = (s) => (s ? String(s).replace(/_/g, ' ') : s);

const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The right-hand detail of a work row as coloured parts: "Blocked · Social · Zhaskia Dela Cruz · Due 2026-06-19".
 * The status takes its status colour (blocked red, stalled amber, waiting on client cyan, in progress sky,
 * review violet, done green), a "stalled" prefix is amber, and the due date is red once past and amber
 * within three days (a finished item's date stays gray). Each part starts capitalised.
 *
 * @param {object} it  A work item.
 * @param {Date} [now]
 * @returns {Array<{text: string, tone: string|null}>}
 */
export const itemMetaParts = (it, now = new Date()) => {
  const done = isDoneStatus(it?.status);
  return [
    it?.stalled && it?.status !== 'stalled' ? { text: 'stalled', tone: 'amber' } : null,
    it?.status ? { text: humanizeStatus(it.status), tone: statusTone(it.status) } : null,
    it?.work_type ? { text: it.work_type, tone: null } : null,
    it?.owner ? { text: it.owner, tone: null } : null,
    it?.due ? { text: `due ${fmtDate(it.due)}`, tone: done ? null : dueTone(it.due, now) } : null,
  ]
    .filter(Boolean)
    .map((part) => ({ ...part, text: upperFirst(String(part.text)) }));
};

/** The right-hand meta of a row as one string: "Blocked · Social · Zhaskia Dela Cruz" (each part starting capitalised). */
export const itemMeta = (it) => itemMetaParts(it).map((part) => part.text).join(' · ');

/**
 * @param {object} props
 * @param {object|null} [props.work]        fn_client_context.work
 * @param {boolean} [props.loading]
 * @param {*} [props.error]                 Truthy when fn_client_context failed.
 * @param {() => void} [props.onRetry]
 * @param {React.ReactNode} [props.action]  Header action (e.g. the drill link).
 * @param {string} [props.itemsEmptyMessage] Shown instead of the rows when a search matched none of them.
 * @param {(bucket: string) => object|null} [props.drillFor]  The drill for a count bucket
 *   (overdue · blocked · stalled · needs_assistance · waiting_on_client · open), or null —
 *   a bucket with no confirmed drill is plain text.
 * @param {(drill: object) => void} [props.onDrill]
 */
export default function WorkCard({ work, loading, error, onRetry, action = null, itemsEmptyMessage = null, drillFor, onDrill }) {
  const items = Array.isArray(work?.items) ? work.items : [];
  const chips = countChips(work);
  // The selected count chip filters the list below it; clicking it again clears the filter.
  const [activeKey, setActiveKey] = useState(null);
  const activeChip = chips.find((c) => c.key === activeKey) || null;
  const shownItems = activeChip ? items.filter((it) => itemInBucket(it, activeChip.key)) : items;
  // Runs every render, before the early returns, to satisfy rules-of-hooks.
  const { pageRows, page, setPage, pageCount, pageSize, total } = usePaged(shownItems, activeChip?.key, CARD_PAGE_SIZE);
  const services = serviceLine(work);

  return (
    <PanelErrorBoundary label="Work">
      <section aria-label="Work" className={SURFACE}>
        <CardHeader title="Work we owe them" action={action} />
        {loading ? (
          <div className="mt-3" role="status" aria-label="Loading">
            <SkeletonText lines={3} lineClass="h-10" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : error ? (
          <BlockError error={error} what="this client's work" message="Couldn't load this client's work." onRetry={onRetry} />
        ) : !work ? (
          <EmptyState variant="inset" message="No work information for this client." />
        ) : (
          <>
            {chips.length > 0 ? (
              <ul className="mt-4 flex flex-wrap gap-2">
                {chips.map((c) => {
                  const active = activeChip?.key === c.key;
                  const drill = drillFor?.(c.key) || null;
                  return (
                    <li
                      key={c.key}
                      data-testid="work-chip"
                      data-tint={c.tint}
                      data-active={active || undefined}
                      className={`rounded-lg border text-sm font-medium transition-colors ${active ? SOLID[c.tint] || SOLID.neutral : `${tintClasses(c.tint).surface} ${tintClasses(c.tint).text}`}`}
                    >
                      {drill ? (
                        <DrillNumber drill={drill} label={`See the ${fmtNumber(c.count)} ${c.label.toLowerCase()}`} onOpen={onDrill} className="block px-3 py-1.5">
                          {fmtNumber(c.count)} {c.label}
                        </DrillNumber>
                      ) : (
                        <button
                          type="button" aria-pressed={active} onClick={() => setActiveKey(active ? null : c.key)}
                          title={active ? 'Show all work again' : `Show only ${c.label.toLowerCase()} work`}
                          className="block rounded-lg px-3 py-1.5 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                        >
                          {fmtNumber(c.count)} {c.label}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState variant="inset" message="No open work for this client." />
            )}
            {services && <p className="mt-2.5 text-sm text-gray-500 dark:text-gray-400">{services}</p>}
            {items.length > 0 ? (
              <div className="mt-4 space-y-2.5">
                {pageRows.map((it, i) => (
                  // One line: the stalled sentence is the row's tooltip, since the attention
                  // strip already says it in full.
                  <RailRow
                    key={it?.id || i} testId="work-row" rail={workItemTint(it)}
                    title={it?.title || 'Untitled work'} meta={itemMetaParts(it)}
                    hint={it?.stalled && it?.stalled_reason ? it.stalled_reason : undefined}
                  />
                ))}
                <Pager page={page} pageCount={pageCount} pageSize={pageSize} total={total} onPage={setPage} />
                <p data-testid="work-sample-note" className="text-xs text-gray-500 dark:text-gray-400">
                  {!activeChip
                    ? 'A sample — the counts above are the full total.'
                    : shownItems.length > 0
                      ? `Showing the ${fmtNumber(shownItems.length)} loaded ${shownItems.length === 1 ? 'item' : 'items'} that match “${activeChip.label}” — the ${fmtNumber(activeChip.count)} above is the full total.`
                      : `None of the ${fmtNumber(items.length)} loaded items match “${activeChip.label}” — the ${fmtNumber(activeChip.count)} above are not in the sample.`}
                </p>
              </div>
            ) : itemsEmptyMessage ? (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{itemsEmptyMessage}</p>
            ) : null}
          </>
        )}
      </section>
    </PanelErrorBoundary>
  );
}
